import { lazy, Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminLayout } from '@/app/layouts/AdminLayout';
import { TenantPortalLayout } from '@/app/layouts/TenantPortalLayout';
import { PageSkeleton } from '@/components/feedback/PageSkeleton';
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequireMallScope,
  RequirePermission,
  RequireUserType,
} from './guards';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { NAV_SECTIONS } from '@/app/navigation/nav.config';

/**
 * ROTA AĞACI.
 *
 * KOD BÖLME (code splitting) — neden her sayfa `lazy`:
 *
 * Tek parça halinde derlenen uygulamada ApexCharts (~158 kB gz) ve tüm
 * ekranların kodu, kullanıcı hangi sayfaya giderse gitsin ilk açılışta
 * indirilir. Grafik kütüphanesini yalnızca grafik içeren sayfalar
 * çektiği için `lazy` sınırı, ilk yükleme bütçesini (250 kB gz) tek
 * başına ayakta tutan şeydir.
 *
 * Ayrıca kiracı portalı kullanıcısı yönetim arayüzünün hiçbir parçasını
 * indirmez — bu yalnızca performans değil, saldırı yüzeyi meselesidir.
 */

/**
 * Menüdeki yol → gerekli yetki eşlemesi.
 *
 * NEDEN MENÜDEN TÜRETİLİYOR: Yetkiyi hem menüde hem rota kaydında ayrı
 * ayrı yazmak, ikisinin zamanla ayrışmasını garanti eder — menüde
 * gizlenen bir ekran, adres çubuğuna yazıldığında açılmaya devam eder.
 * Tek kaynak menü yapılandırmasıdır.
 */
const PERMISSION_BY_PATH = new Map<string, string | string[]>(
  NAV_SECTIONS.flatMap((section) =>
    section.items.flatMap((item) =>
      item.to && item.permission
        ? [[item.to.replace(/^\//, ''), item.permission] as [string, string | string[]]]
        : [],
    ),
  ),
);

/**
 * TEK AVM ZORUNLU EKRANLAR.
 *
 * NEDEN MENÜDEKİ `scope` ALANINDAN TÜRETİLMİYOR: `scope: 'mall'` işareti
 * "bu ekran tek bir AVM bağlamında anlamlıdır" der ve 30 ekranda bulunur —
 * ünite listesi, kiracı listesi, talepler… Bunların hepsi portföy modunda
 * OKUNABİLİR ve kapatmak, konsolide görünümü işlevsiz kılardı.
 *
 * Buradaki liste daha dar bir şeyi ifade eder: ekranın VAR OLMA SEBEBİ
 * kayıt yazmaktır. Beyan girişi ve toplu yükleme portföy modunda
 * doldurulabilir ama kaydedilemez; kullanıcı bunu formu doldurduktan
 * SONRA öğreniyordu. Liste kısa ve açık tutuluyor: bir ekran buraya
 * yalnızca okuma tarafı boşsa girer.
 */
const SINGLE_MALL_ONLY = new Set(['ciro/beyan-girisi', 'ciro/excel-yukleme']);

/**
 * `lazy` + `Suspense` + yetki sarmalayıcısı.
 *
 * `path` verildiğinde o yolun menüdeki yetkisi otomatik uygulanır;
 * rota kaydına ayrıca yetki yazmak gerekmez.
 */
function page(
  loader: () => Promise<{ default: ComponentType }>,
  path?: string,
): ReactElement {
  const Component = lazy(loader);

  const element = (
    <Suspense fallback={<PageSkeleton />}>
      <Component />
    </Suspense>
  );

  const scoped =
    path && SINGLE_MALL_ONLY.has(path) ? (
      <RequireMallScope>{element}</RequireMallScope>
    ) : (
      element
    );

  const permission = path ? PERMISSION_BY_PATH.get(path) : undefined;
  if (!permission) return scoped;

  // Yetki kontrolü DIŞTA: erişemeyeceği bir ekranda "AVM seçin" demek,
  // kullanıcıya yapamayacağı bir işi yapmasını söylemektir.
  return <RequirePermission permission={permission}>{scoped}</RequirePermission>;
}

// Açık tip anotasyonu gerekli: React Router'ın dönüş tipi iç bir pakete
// (@remix-run/router) referans verdiği için TypeScript onu isimlendiremiyor.
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  /**
   * KİRACI PORTALI — AYRI ROUTE AĞACI.
   *
   * Yönetim arayüzüyle aynı ağaçta DEĞİL. Her iki kökte `RequireUserType`
   * guard'ı bulunur: personel token'ı portala, kiracı token'ı yönetime
   * giremez. Ayrı ağaç, ileride paylaşılan bir bileşenin yanlışlıkla
   * kiracıya personel verisi göstermesini yapısal olarak engeller.
   *
   * Sunucu tarafında aynı ayrım @StaffOnly / @TenantOnly ile zorlanır;
   * buradaki guard yalnızca kullanıcıyı doğru ekrana götürür.
   */
  {
    path: '/portal',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireAuth>
        <RequireUserType type="TENANT">
          <TenantPortalLayout />
        </RequireUserType>
      </RequireAuth>
    ),
    children: [
      { index: true, element: page(() => import('@/features/portal/pages/PortalHomePage')) },
      { path: 'ciro', element: page(() => import('@/features/portal/pages/PortalTurnoverPage'), 'ciro') },
      {
        path: 'faturalar',
        element: page(() => import('@/features/portal/pages/PortalInvoicesPage'), 'faturalar'),
      },
      {
        path: 'talepler',
        element: page(() => import('@/features/portal/pages/PortalTicketsPage'), 'talepler'),
      },
      {
        path: 'duyurular',
        element: page(() => import('@/features/portal/pages/PortalAnnouncementsPage'), 'duyurular'),
      },
    ],
  },

  {
    path: '/giris',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RedirectIfAuthenticated>
        {page(() => import('@/features/auth/pages/LoginPage'))}
      </RedirectIfAuthenticated>
    ),
  },

  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireAuth>
        <RequireUserType type="STAFF">
          <AdminLayout />
        </RequireUserType>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      { path: 'dashboard', element: page(() => import('@/features/dashboard/pages/DashboardPage'), 'dashboard') },
      {
        path: 'dashboard/ciro',
        element: page(() => import('@/features/turnover/pages/TurnoverAnalyticsPage'), 'dashboard/ciro'),
      },

      {
        path: 'portfoy/uniteler',
        element: page(() => import('@/features/property/pages/UnitsPage'), 'portfoy/uniteler'),
      },
      {
        path: 'portfoy/doluluk',
        element: page(() => import('@/features/property/pages/OccupancyPage'), 'portfoy/doluluk'),
      },
      {
        path: 'portfoy/avmler',
        element: page(() => import('@/features/property/pages/MallsPage'), 'portfoy/avmler'),
      },

      { path: 'kiracilar', element: page(() => import('@/features/tenants/pages/TenantsPage'), 'kiracilar') },
      {
        path: 'kiracilar/markalar',
        element: page(() => import('@/features/tenants/pages/BrandsPage'), 'kiracilar/markalar'),
      },
      {
        path: 'kiracilar/kategoriler',
        element: page(() => import('@/features/tenants/pages/CategoriesPage'), 'kiracilar/kategoriler'),
      },

      { path: 'sozlesmeler', element: page(() => import('@/features/leases/pages/LeasesPage'), 'sozlesmeler') },
      {
        path: 'sozlesmeler/bitisler',
        element: page(() => import('@/features/leases/pages/LeaseExpiryPage'), 'sozlesmeler/bitisler'),
      },
      {
        path: 'sozlesmeler/teminatlar',
        element: page(() => import('@/features/leases/pages/SecuritiesPage'), 'sozlesmeler/teminatlar'),
      },

      {
        path: 'ciro/beyanlar',
        element: page(() => import('@/features/turnover/pages/TurnoverDeclarationsPage'), 'ciro/beyanlar'),
      },
      {
        path: 'ciro/beyan-girisi',
        element: page(() => import('@/features/turnover/pages/TurnoverEntryPage'), 'ciro/beyan-girisi'),
      },
      {
        path: 'ciro/onay-kuyrugu',
        element: page(() => import('@/features/turnover/pages/TurnoverApprovalsPage'), 'ciro/onay-kuyrugu'),
      },
      {
        path: 'ciro/excel-yukleme',
        element: page(() => import('@/features/turnover/pages/TurnoverImportPage'), 'ciro/excel-yukleme'),
      },
      {
        path: 'ciro/ciro-kirasi',
        element: page(() => import('@/features/turnover/pages/TurnoverRentPage'), 'ciro/ciro-kirasi'),
      },

      {
        path: 'finans/tahakkuk',
        element: page(() => import('@/features/billing/pages/AccrualPage'), 'finans/tahakkuk'),
      },
      {
        path: 'finans/faturalar',
        element: page(() => import('@/features/billing/pages/InvoicesPage'), 'finans/faturalar'),
      },
      {
        path: 'finans/tahsilatlar',
        element: page(() => import('@/features/billing/pages/CollectionsPage'), 'finans/tahsilatlar'),
      },
      { path: 'finans/oag', element: page(() => import('@/features/cam/pages/CamPage'), 'finans/oag') },
      {
        path: 'finans/cari-hesap',
        element: page(() => import('@/features/billing/pages/StatementPage'), 'finans/cari-hesap'),
      },

      {
        path: 'operasyon/talepler',
        element: page(() => import('@/features/operations/pages/TicketsPage'), 'operasyon/talepler'),
      },
      {
        path: 'operasyon/bakim',
        element: page(() => import('@/features/operations/pages/MaintenancePage'), 'operasyon/bakim'),
      },
      {
        path: 'operasyon/sayaclar',
        element: page(() => import('@/features/operations/pages/MetersPage'), 'operasyon/sayaclar'),
      },
      {
        path: 'operasyon/is-emirleri',
        element: page(() => import('@/features/operations/pages/WorkOrdersPage'), 'operasyon/is-emirleri'),
      },
      {
        path: 'operasyon/varliklar',
        element: page(() => import('@/features/operations/pages/AssetsPage'), 'operasyon/varliklar'),
      },

      {
        path: 'analitik/ziyaretci',
        element: page(() => import('@/features/analytics/pages/FootfallPage'), 'analitik/ziyaretci'),
      },

      {
        path: 'pazarlama/etkinlikler',
        element: page(() => import('@/features/marketing/pages/EventsPage'), 'pazarlama/etkinlikler'),
      },
      {
        path: 'pazarlama/gecici-kiralama',
        element: page(() => import('@/features/marketing/pages/SpecialtyLeasingPage'), 'pazarlama/gecici-kiralama'),
      },

      {
        path: 'guvenlik/olaylar',
        element: page(() => import('@/features/compliance/pages/IncidentsPage'), 'guvenlik/olaylar'),
      },
      {
        path: 'guvenlik/belgeler',
        element: page(() => import('@/features/compliance/pages/DocumentsPage'), 'guvenlik/belgeler'),
      },

      { path: 'raporlar', element: page(() => import('@/features/reports/pages/ReportsPage'), 'raporlar') },

      {
        path: 'sistem/parametreler',
        element: page(() => import('@/features/admin/pages/SystemParametersPage'), 'sistem/parametreler'),
      },
      {
        path: 'sistem/kullanicilar',
        element: page(() => import('@/features/admin/pages/UsersPage'), 'sistem/kullanicilar'),
      },
      { path: 'sistem/roller', element: page(() => import('@/features/admin/pages/RolesPage'), 'sistem/roller') },
      {
        path: 'sistem/bildirimler',
        element: page(() => import('@/features/admin/pages/NotificationsPage'), 'sistem/bildirimler'),
      },
      {
        path: 'sistem/demo-veri',
        element: page(() => import('@/features/admin/pages/DemoDataPage'), 'sistem/demo-veri'),
      },
      {
        path: 'sistem/denetim',
        element: page(() => import('@/features/admin/pages/AuditLogPage'), 'sistem/denetim'),
      },

      // Henüz rotası tanımlanmamış her yol panoya yönlenir.
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
